import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Video, Link2, Copy, MessageCircle, Play, CheckCircle2, Save } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnamneseTab } from "@/components/medico/AnamneseTab";
import { MemedEmbed } from "@/components/medico/MemedEmbed";
import { AmbientScribe } from "@/components/medico/AmbientScribe";
import { VideoCall } from "@/components/telemed/VideoCall";
import { useAuth } from "@/store/AuthContext";
import { useClinic } from "@/store/ClinicContext";

export const Telemedicina = () => {
  const { currentUser } = useAuth();
  const { appointments, patients, addRecord } = useClinic();
  const [patientId, setPatientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [started, setStarted] = useState(false);
  const [anamnese, setAnamnese] = useState("");
  const [remoteStream, setRemoteStream] = useState(null);
  const appendScribe = (t) => setAnamnese((p) => (p.trim() ? `${p.trimEnd()}\n\n${t}` : t));

  const paidAppts = useMemo(
    () => (appointments || []).filter((a) => (a.charge_status === "paid" || a.paid) && a.status !== "cancelado"),
    [appointments]
  );
  const sel = paidAppts.find((a) => a.id === selectedId) || null;
  const roomId = sel ? (sel.telemed_room || sel.id) : "";
  const link = roomId ? `${window.location.origin}/sala/${roomId}` : "";

  useEffect(() => {
    if (!sel) { setPatientId(""); return; }
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();
    const tail = (s) => (s || "").replace(/\D/g, "").slice(-8);
    const byName = (patients || []).filter((p) => norm(p.name) === norm(sel.patient));
    const ph = tail(sel.patient_phone);
    const match = (ph && byName.find((p) => tail(p.phone) === ph)) || (byName.length === 1 ? byName[0] : null)
      || (ph ? (patients || []).find((p) => tail(p.phone) === ph) : null);
    setPatientId(match?.id || "");
  }, [sel, patients]);

  const saveRecord = async () => {
    if (!patientId) return toast.error("Selecione o paciente do prontuário antes de salvar.");
    if (!anamnese.trim()) return toast.error("Escreva a evolução clínica antes de salvar.");
    setSaving(true);
    try {
      await addRecord(patientId, anamnese.trim(), sel?.id || "");
      setAnamnese("");
      toast.success("Atendimento salvo no histórico do paciente");
    } catch (e) {
      toast.error(e.message || "Falha ao salvar atendimento");
    } finally {
      setSaving(false);
    }
  };

  const copy = () => { if (!link) return; navigator.clipboard?.writeText(link); toast.success("Link copiado!"); };
  const openRoom = () => { if (link) window.open(link, "_blank", "noopener"); };
  const wa = () => {
    if (!sel) return;
    const digits = (sel.patient_phone || "").replace(/\D/g, "");
    const to = digits ? (digits.length <= 11 ? `55${digits}` : digits) : "";
    const msg = `Olá ${sel.patient}! Este é o link da sua teleconsulta VeloClin. Clique para entrar na sala no horário agendado: ${link}`;
    window.open(to ? `https://wa.me/${to}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    toast.success("Abrindo WhatsApp para enviar o link ao paciente");
  };
  const startCall = () => {
    if (!sel) return toast.error("Selecione um paciente com pagamento confirmado.");
    setStarted(true);
    toast.success("Sala de telemedicina pronta", { description: "Envie o link ao paciente e aguarde ele entrar." });
  };
  const end = () => { setStarted(false); toast("Chamada encerrada"); };

  if (!started)
    return (
      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="telemed-start-card">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><Video className="h-7 w-7" /></div>
        <h3 className="text-center font-heading text-lg font-bold text-slate-900">Telemedicina Integrada</h3>
        <p className="mx-auto mt-1 max-w-md text-center text-sm text-slate-500">
          Selecione um paciente com <b>pagamento confirmado</b> para gerar a sala e enviar o link da teleconsulta.
        </p>

        {paidAppts.length === 0 ? (
          <div className="mx-auto mt-5 max-w-md rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-sm text-amber-800" data-testid="telemed-no-paid">
            Nenhum paciente com pagamento confirmado no momento. Gere uma cobrança em “Minha Agenda” e confirme o pagamento para liberar a teleconsulta.
          </div>
        ) : (
          <div className="mx-auto mt-5 max-w-md space-y-3">
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger data-testid="telemed-patient-select"><SelectValue placeholder="Selecione o paciente (pagamento confirmado)" /></SelectTrigger>
              <SelectContent>
                {paidAppts.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`telemed-opt-${a.id}`}>
                    {a.patient}{a.date ? ` · ${a.date}` : ""}{a.time ? ` ${a.time}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {sel && (
              <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Pagamento confirmado</div>
                <code data-testid="telemed-link" className="truncate font-mono text-xs font-semibold text-blue-700">{link}</code>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="telemed-copy-btn" onClick={copy} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Copy className="h-3.5 w-3.5" />Copiar link</button>
                  <button data-testid="telemed-wa-btn" onClick={wa} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><MessageCircle className="h-3.5 w-3.5" />Enviar por WhatsApp</button>
                </div>
              </div>
            )}

            <button data-testid="start-telemed-btn" onClick={startCall} disabled={!sel} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"><Play className="h-4 w-4" />Iniciar teleconsulta</button>
          </div>
        )}
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50/60 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2 text-sm">
          <Link2 className="h-4 w-4 shrink-0 text-blue-600" />
          <span className="shrink-0 text-xs font-semibold text-slate-500">{sel?.patient} ·</span>
          <code data-testid="telemed-link" className="truncate font-mono text-xs font-semibold text-blue-700">{link}</code>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button data-testid="telemed-open-btn" onClick={openRoom} className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
            <Video className="h-3.5 w-3.5" />Abrir sala
          </button>
          <button data-testid="telemed-copy-btn" onClick={copy} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
            <Copy className="h-3.5 w-3.5" />Copiar
          </button>
          <button data-testid="telemed-wa-btn" onClick={wa} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
            <MessageCircle className="h-3.5 w-3.5" />WhatsApp
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Vídeo ao vivo (WebRTC) */}
        <VideoCall roomId={roomId} role="medico" displayName={currentUser?.name || "Médico"} peerLabel={sel?.patient || "o paciente"} onEnd={end} onRemoteStream={setRemoteStream} />
        <AmbientScribe onResult={appendScribe} extraStream={remoteStream} />

        {/* Prontuário simultâneo */}
        <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Prontuário em tempo real</p>
          <Tabs defaultValue="anamnese">
            <TabsList className="mb-4 grid w-full grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
              <TabsTrigger value="anamnese" className="rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 sm:text-sm">Evolução</TabsTrigger>
              <TabsTrigger value="receituario" data-testid="tab-receituario" className="rounded-lg text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700 sm:text-sm">Prescrição &amp; Exames</TabsTrigger>
            </TabsList>
            <TabsContent value="anamnese">
              <AnamneseTab value={anamnese} onChange={setAnamnese} />
              <div className="mt-3 flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center">
                <Select value={patientId} onValueChange={setPatientId}>
                  <SelectTrigger data-testid="telemed-record-patient-select" className="sm:flex-1"><SelectValue placeholder="Paciente do prontuário" /></SelectTrigger>
                  <SelectContent>
                    {(patients || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`telemed-record-patient-${p.id}`}>{p.name}{p.phone ? ` · ${p.phone}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button data-testid="telemed-save-record-btn" onClick={saveRecord} disabled={saving} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"><Save className="h-4 w-4" />{saving ? "Salvando..." : "Salvar atendimento"}</button>
              </div>
            </TabsContent>
            <TabsContent value="receituario">
              <MemedEmbed />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
